import { useState } from 'react'
import {
  SubstanceCategories,
  type SubstanceOfConcern,
  type SubstanceCategory,
} from '@esf/shared'

interface SubstancesOfConcernFormProps {
  substances: SubstanceOfConcern[]
  onChange: (substances: SubstanceOfConcern[]) => void
  disabled?: boolean
}

const SubstanceCategoryLabels: Record<SubstanceCategory, string> = {
  SVHC: 'SVHC (REACH)',
  CLP_HAZARDOUS: 'CLP Hazardous',
  CIRCULARITY_INHIBITOR: 'Circularity Inhibitor',
  POP: 'Persistent Organic Pollutant',
}

const emptySubstance: SubstanceOfConcern = {
  chemicalName: '',
  casNumber: '',
  ecNumber: '',
  category: 'SVHC',
  locationInProduct: '',
  concentrationPercent: 0,
  safeUseInstructions: '',
  disposalInstructions: '',
}

export default function SubstancesOfConcernForm({
  substances,
  onChange,
  disabled,
}: SubstancesOfConcernFormProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const addSubstance = () => {
    onChange([...substances, { ...emptySubstance }])
    setExpandedIndex(substances.length)
  }

  const removeSubstance = (index: number) => {
    const updated = substances.filter((_, i) => i !== index)
    onChange(updated)
    if (expandedIndex === index) {
      setExpandedIndex(null)
    }
  }

  const updateSubstance = (index: number, field: keyof SubstanceOfConcern, value: string | number) => {
    const updated = substances.map((s, i) =>
      i === index ? { ...s, [field]: value } : s
    )
    onChange(updated)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-700">Substances of Concern</h4>
          <p className="text-xs text-gray-500">ESPR Article 7 - SVHC, CLP, POPs</p>
        </div>
        <button
          type="button"
          onClick={addSubstance}
          disabled={disabled}
          className="btn btn-secondary text-sm py-1 px-3"
        >
          + Add Substance
        </button>
      </div>

      {substances.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="mt-2 text-sm text-gray-500">No substances of concern declared</p>
          <p className="text-xs text-gray-400">Click "Add Substance" to declare any regulated substances</p>
        </div>
      ) : (
        <div className="space-y-3">
          {substances.map((substance, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Header */}
              <div
                className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer"
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
              >
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    substance.category === 'SVHC' ? 'bg-red-100 text-red-800' :
                    substance.category === 'CLP_HAZARDOUS' ? 'bg-orange-100 text-orange-800' :
                    substance.category === 'POP' ? 'bg-purple-100 text-purple-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {SubstanceCategoryLabels[substance.category]}
                  </span>
                  <span className="font-medium text-sm">
                    {substance.chemicalName || 'Unnamed substance'}
                  </span>
                  {substance.casNumber && (
                    <span className="text-xs text-gray-500">CAS: {substance.casNumber}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {substance.concentrationPercent > 0 && (
                    <span className="text-xs text-gray-500">{substance.concentrationPercent}%</span>
                  )}
                  <svg
                    className={`h-5 w-5 text-gray-400 transition-transform ${
                      expandedIndex === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedIndex === index && (
                <div className="px-4 py-4 space-y-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Chemical Name (IUPAC) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={substance.chemicalName}
                        onChange={e => updateSubstance(index, 'chemicalName', e.target.value)}
                        disabled={disabled}
                        className="filter-input w-full"
                        placeholder="e.g., Bis(2-ethylhexyl) phthalate"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        value={substance.category}
                        onChange={e => updateSubstance(index, 'category', e.target.value)}
                        disabled={disabled}
                        className="filter-select w-full"
                      >
                        {SubstanceCategories.map(cat => (
                          <option key={cat} value={cat}>{SubstanceCategoryLabels[cat]}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CAS Number</label>
                      <input
                        type="text"
                        value={substance.casNumber || ''}
                        onChange={e => updateSubstance(index, 'casNumber', e.target.value)}
                        disabled={disabled}
                        className="filter-input w-full"
                        placeholder="e.g., 117-81-7"
                      />
                      <p className="text-xs text-gray-500 mt-1">Chemical Abstracts Service</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">EC Number</label>
                      <input
                        type="text"
                        value={substance.ecNumber || ''}
                        onChange={e => updateSubstance(index, 'ecNumber', e.target.value)}
                        disabled={disabled}
                        className="filter-input w-full"
                        placeholder="e.g., 204-211-0"
                      />
                      <p className="text-xs text-gray-500 mt-1">European Community</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Concentration (%)</label>
                      <input
                        type="number"
                        value={substance.concentrationPercent}
                        onChange={e => updateSubstance(index, 'concentrationPercent', parseFloat(e.target.value) || 0)}
                        disabled={disabled}
                        className="filter-input w-full"
                        min="0"
                        max="100"
                        step="0.001"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location in Product</label>
                    <input
                      type="text"
                      value={substance.locationInProduct}
                      onChange={e => updateSubstance(index, 'locationInProduct', e.target.value)}
                      disabled={disabled}
                      className="filter-input w-full"
                      placeholder="e.g., Plastic coating on armrest"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Safe Use Instructions</label>
                    <textarea
                      value={substance.safeUseInstructions}
                      onChange={e => updateSubstance(index, 'safeUseInstructions', e.target.value)}
                      disabled={disabled}
                      className="filter-input w-full min-h-[60px] resize-y"
                      rows={2}
                      placeholder="Instructions for safe handling..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Disposal Instructions</label>
                    <textarea
                      value={substance.disposalInstructions}
                      onChange={e => updateSubstance(index, 'disposalInstructions', e.target.value)}
                      disabled={disabled}
                      className="filter-input w-full min-h-[60px] resize-y"
                      rows={2}
                      placeholder="End-of-life disposal guidance..."
                    />
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => removeSubstance(index)}
                      disabled={disabled}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove Substance
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
